import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import {
  Location,
  NavigateFunction,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import { PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import {
  bulkDeletePlaylists,
  bulkDeleteTracks,
} from '@disco/state-management/actions/ServerActions'
import {
  getExplorerSelectedItems,
  getExplorerSelectedTracks,
  getShortlistsSelectedItems,
} from '@disco/state-management/selectors/selection'
import { Track } from '@disco/types/types/track'

import DocumentHeadTags from 'components/document-head-tags'
import ModalDialog from 'components/ui/modal-dialog'

import type { RootState } from 'store/configureStore'
import { getSelectionReturnPath } from 'utils/routing'

const mapStateToProps = (state: RootState) => {
  const explorerSelectedItems = getExplorerSelectedItems(state)
  const shortlistsSelectedItems = getShortlistsSelectedItems(state)
  const selectedItems = [...explorerSelectedItems, ...shortlistsSelectedItems]
  return {
    playlistVersionId: state.server.editedTrack.playlistVersionId,
    explorerSelectedTracks: getExplorerSelectedTracks(state).filter(
      (item: Track) => !item.outside_your_territories,
    ),
    shortlistsSelectedTracks: shortlistsSelectedItems.filter(
      item => item.type === TRACK && !item.outside_your_territories,
    ),
    selectedPlaylists: selectedItems.filter(
      item => item.type && item.type === PLAYLIST,
    ),
  }
}

const mapDispatchToProps = {
  bulkDeleteTracks,
  bulkDeletePlaylists,
}

type Props = {
  bulkDeleteTracks: typeof bulkDeleteTracks
  bulkDeletePlaylists: typeof bulkDeletePlaylists
  explorerSelectedTracks: Record<string, any>[]
  shortlistsSelectedTracks: Record<string, any>[]
  selectedPlaylists: Record<string, any>[]
  location: Location
  navigate: NavigateFunction
  searchParams: URLSearchParams
}

class SelectionDelete extends React.Component<Props> {
  _onClose = () => {
    this.props.navigate(getSelectionReturnPath(this.props.location.pathname))
  }
  _handleItemsDelete = () => {
    const {
      explorerSelectedTracks,
      shortlistsSelectedTracks,
      selectedPlaylists,
    } = this.props

    if (explorerSelectedTracks.length > 0) {
      const source =
        explorerSelectedTracks.length === 1
          ? 'Delete action from Explorer action menu (single track)'
          : 'Delete action from Explorer action menu (part of multiple selection)'
      this.props.bulkDeleteTracks(
        explorerSelectedTracks.map(item => item.id),
        source,
        null,
      )
    }

    if (shortlistsSelectedTracks.length > 0) {
      const source =
        shortlistsSelectedTracks.length === 1
          ? 'Delete action from Shortlist action menu (single track)'
          : 'Delete action from Shortlist action menu (part of multiple selection)'
      this.props.bulkDeleteTracks(
        shortlistsSelectedTracks.map(item => item.id),
        source,
        null,
      )
    }

    if (selectedPlaylists.length > 0) {
      this.props.bulkDeletePlaylists(
        selectedPlaylists.map(item => item.playlist_id),
      )
    }

    this._onClose()
  }

  render() {
    return (
      <div>
        <DocumentHeadTags title="Delete selected items | DISCO" />
        <ModalDialog
          title="Delete selected items"
          primaryAction={{
            caption: 'Delete',
            onClick: this._handleItemsDelete,
          }}
          status="danger"
          onCancel={this._onClose}
        >
          {this.props.searchParams.get('source') === 'shortlist' ? (
            <>
              <p>
                Are you sure you want to permanently delete the selected items
                from your DISCO? This can’t be undone.
              </p>
              <p>
                If you’re looking to just remove tracks from a playlist, choose
                “Remove from shortlist”.
              </p>
            </>
          ) : (
            'Are you sure you want to permanently delete the selected items from your DISCO? This can’t be undone.'
          )}
        </ModalDialog>
      </div>
    )
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps)

const ConnectedSelectionDelete = connector(SelectionDelete)

const SelectionDeleteWithNavigation = (props: any) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  return (
    <ConnectedSelectionDelete
      {...props}
      navigate={navigate}
      location={location}
      searchParams={searchParams}
    />
  )
}

type PropsFromRedux = ConnectedProps<typeof connector>

export default SelectionDeleteWithNavigation
